/**
 * Máquina de estados de la pantalla de marcación de Staff (004 · US1 · T029/T030; US8 · T061;
 * FR-002, FR-005, FR-021, FR-032, FR-034, FR-035, FR-036).
 *
 * **Reducer puro**: sin DOM, sin timers y sin relojes. Los instantes llegan en el evento en
 * milisegundos **monotónicos** (`performance.now()`), igual que en el marcador.
 *
 * | fase | qué ve el trabajador |
 * |---|---|
 * | `OPENING` | "Buscando marcador…" mientras el navegador abre la cámara |
 * | `SEARCHING` | cámara abierta, buscando el código del marcador (ayuda a los 8 s, reintento a los 20 s) |
 * | `REQUESTING` | "Verificando…" — el bootstrap ya salió hacia el servidor |
 * | `QUEUED` | "Un momento… Fermin está procesando otras marcaciones." con la cámara abierta |
 * | `CHALLENGE` | "Verificando…" con la barra de progreso siguiendo los frames observados. Con
 *   `autonomous` en `true` (US8) la barra sigue los **slots** en vez de los índices de un challenge — para el
 *   trabajador la pantalla es idéntica |
 * | `VERIFYING` | "Verificando…" — la evidencia ya salió |
 * | `SUCCESS` | "✓ Entrada registrada", hora de la sede, nombre de la sede |
 * | `ACTION_CHANGED` | la acción mostrada ya no es la vigente (US2, T036): muestra la correcta y
 *   **pide confirmación explícita**; hasta que el trabajador confirma no se vuelve a buscar el
 *   marcador ni se reproduce ningún challenge (no llegó a crearse ninguno) |
 * | `ERROR` | el mensaje exacto del catálogo para el código (FR-034) |
 * | `READY` | QA-7 de la 005: terminó la espera de un `429` con `retry_after_seconds` — sin error,
 *   con la cámara abierta **sin leer** y un botón "Marcar" que vuelve a buscar el marcador |
 * | `CAMERA_BLOCKED` | explicación, "Intentar nuevamente" y "Cómo habilitarla" (FR-036) |
 *
 * **Nada de lo que se muestra expone identificadores de challenge, nonces ni conteos de frames**
 * (FR-032): esta máquina guarda el `ChallengeRef` para poder verificar, pero la pantalla solo lee
 * `phase`, `progress` y `mark`.
 *
 * **T087 (FR-045, anti-replay del modo autónomo).** Un `INSUFFICIENT_EVIDENCE` en modo autónomo
 * puede ser el falso positivo de dos trabajadores apuntando a la vez a la misma pantalla: sus
 * ráfagas comparten slots y el segundo cae por el anti-replay (research L6). El umbral no se toca
 * (research §: un atacante puede reenviar subconjuntos de 4-de-6 frames capturados, justo lo que
 * atrapa); lo que sí se resuelve es que una ráfaga dura menos de un segundo, así que un reintento
 * unos segundos después ya no solapa. Mientras `autonomousRetryUntil` no es `null`, Staff sigue en
 * `CHALLENGE`/`autonomous` (la cámara nunca se toca) pero **descarta** los frames que lleguen y
 * muestra un mensaje de espera honesto en vez del error; pasado el plazo, vuelve a capturar desde
 * cero con evidencia nueva. Es como máximo **un** reintento automático por intento (lo administra
 * `use-marking.ts`); un segundo rechazo sí termina en `ERROR`, con el reintento manual de siempre.
 *
 * **QA-7 de la 005 (fin de la cuenta regresiva de un `429`).** Un `RATE_LIMITED`/`TERMINAL_BUSY`
 * con `retry_after_seconds` entra en `ERROR` con el reintento deshabilitado (T094). Antes, al
 * cumplirse el plazo solo se habilitaba el botón y el mensaje de error se quedaba pegado ("No
 * pudimos marcar · Demasiadas solicitudes…") aunque ya no hubiera nada que esperar. Ahora el tick
 * que cruza `retryDisabledUntil` lleva a `READY`: el error desaparece y la pantalla ofrece
 * "Marcar". **No** se vuelve a `SEARCHING` sola: con la cámara todavía apuntando al marcador eso
 * crearía una marca que el trabajador no pidió (en el caso del intervalo autónomo, justo la
 * segunda marca que el servidor acababa de rechazar). Volver a buscar exige tocar "Marcar"
 * (evento `retry`).
 */
import type {
  AttendanceAction,
  AttendanceMarkConfirmation,
  ChallengeRef,
} from '@fermin/contracts';

import {
  autonomousProgress,
  emptyAutonomousCollection,
  type AutonomousCollection,
  type CameraFailure,
} from '../../camera';

import { emptyCollection, evidenceProgress, type ChallengeShape, type FrameCollection } from './frames';


/** Ayuda contextual de FR-005: a los 8 s buscando, invitando a **separar** el teléfono. */
export const HELP_AFTER_MS = 8_000;

/** Reintento ofrecido de FR-005: a los 20 s buscando. */
export const RETRY_AFTER_MS = 20_000;

/**
 * T087/FR-045: plazo de espera del reintento automático tras un rechazo del modo autónomo por
 * evidencia insuficiente. Con los valores por defecto del protocolo
 * (`AUTONOMOUS_MIN_FRAMES = 4`, `AUTONOMOUS_SLOT_MS = 200`, hueco máximo de 2 slots entre frames
 * consecutivos — `apps/api/src/modules/vpp/autonomous.ts`), la ráfaga más larga que el servidor
 * puede aceptar mide `(minFrames − 1) × maxStep × slotMs = 3 × 2 × 200 ms = 1200 ms`. 2000 ms deja
 * ~800 ms de margen sobre ese peor caso por defecto: pasado ese plazo el reloj real ya avanzó más
 * allá de cualquier slot de la ráfaga rechazada, así que una captura nueva no puede compartir los
 * `AUTONOMOUS_REPLAY_MIN_SHARED = 2` slots que dispara el anti-replay (FR-045). Es también del
 * orden de "unos segundos" que describe la decisión del usuario, no una espera imperceptible ni
 * una que se sienta rota.
 */
export const AUTONOMOUS_RETRY_DELAY_MS = 2_000;

/** FR-035/US7 AC4: tras 3 challenges fallidos seguidos se sugiere contactar al administrador. */
export const FAILED_ATTEMPTS_BEFORE_SUPPORT = 3;

/** US7 AC4: los tres fallos cuentan solo si caen dentro de esta ventana ("en 2 minutos"). */
export const SUPPORT_WINDOW_MS = 120_000;

export type MarkingPhase =
  | 'OPENING'
  | 'SEARCHING'
  | 'REQUESTING'
  | 'QUEUED'
  | 'CHALLENGE'
  | 'VERIFYING'
  | 'SUCCESS'
  | 'ACTION_CHANGED'
  | 'ERROR'
  | 'READY'
  | 'CAMERA_BLOCKED';

/** Error ya traducido al catálogo de FR-034; el detalle técnico no llega nunca hasta aquí. */
export interface MarkingError {
  /** Clave de `attendance.errors.*` / `common.errors.*`; nunca el mensaje crudo de la API. */
  code: string;
  message: string;
  /** `true` si tiene sentido volver a apuntar la cámara sin salir de la pantalla. */
  recoverable: boolean;
  /**
   * `details.retry_after_seconds` de `RATE_LIMITED` y `TERMINAL_BUSY` (T094 · wiring §3.2):
   * mientras el servidor pide esperar, "Intentar nuevamente" se deshabilita ese mismo tiempo en
   * vez de invitar a un reintento que el servidor va a rechazar igual. `undefined` en cualquier
   * otro código: ahí el reintento queda habilitado de inmediato, como siempre.
   */
  retryAfterSeconds?: number;
}

export interface MarkingState {
  phase: MarkingPhase;
  /** Acción que Staff mostró; viaja en `POST /challenges` solo para detectar discrepancia (FR-007). */
  proposedAction: AttendanceAction | null;
  /** Challenge en curso; `null` fuera de uno (o mientras se captura en modo autónomo). **No se
   * muestra nunca** (FR-032). */
  challenge: ChallengeRef | null;
  /** Posición y espera estimada mientras el marcador está ocupado (FR-013). */
  queue: { position: number; etaMs: number } | null;
  frames: FrameCollection;
  /** `true` si la captura en curso es de frames `FM1A` del modo sin conexión (US8, T061): mismo
   * `phase` `CHALLENGE`, evidencia distinta. Para el trabajador no hay ninguna diferencia visible. */
  autonomous: boolean;
  /** Frames autónomos acumulados; `null` fuera del modo autónomo. */
  autonomousFrames: AutonomousCollection | null;
  /**
   * T087/FR-045: `nowMs` en el que se reanuda la captura tras un reintento automático del modo
   * autónomo; `null` fuera de esa espera. No nulo ⟹ `phase === 'CHALLENGE'` y `autonomous === true`,
   * pero los frames que lleguen se descartan hasta cumplirse el plazo (ver `isRetryingAutonomous`).
   */
  autonomousRetryUntil: number | null;
  /** Clave de idempotencia del intento en curso (FR-035a); se repite en los reintentos. */
  idempotencyKey: string | null;
  mark: AttendanceMarkConfirmation | null;
  error: MarkingError | null;
  cameraFailure: CameraFailure | null;
  /** Instante monotónico en que empezó a buscarse el marcador; `null` fuera de `SEARCHING`. */
  searchStartedAt: number | null;
  /** Instante monotónico actual, para los plazos de FR-005. */
  nowMs: number;
  /** Challenges fallidos consecutivos en esta pantalla (FR-035; se resetea al verificar con éxito). */
  failedAttempts: number;
  /** Instantes (`nowMs`) de los fallos recientes, para exigir que caigan en la misma ventana de
   * `SUPPORT_WINDOW_MS` (US7 AC4: "tres challenges fallidos consecutivos **en 2 minutos**"). */
  failureAt: number[];
  /**
   * T094 (`RATE_LIMITED`/`TERMINAL_BUSY`, `details.retry_after_seconds`): `nowMs` en el que vuelve
   * a habilitarse "Intentar nuevamente"; `null` cuando no hay espera pendiente. Se fija al entrar
   * en `ERROR` desde un fallo con `retryAfterSeconds` y se limpia en cualquier transición que salga
   * de `ERROR` (igual que `error`).
   */
  retryDisabledUntil: number | null;
}

export type MarkingEvent =
  /** El permiso ya estaba concedido o el trabajador aceptó la explicación. */
  | { type: 'request_camera' }
  | { type: 'camera_opened'; now: number }
  | { type: 'camera_failed'; failure: CameraFailure; error: MarkingError }
  /** Se leyó un Bootstrap Code y salió `POST /challenges`. */
  | { type: 'bootstrap_found' }
  | { type: 'challenge_created'; challenge: ChallengeRef; idempotencyKey: string }
  | { type: 'challenge_queued'; challenge: ChallengeRef; position: number; etaMs: number }
  /**
   * La posición en cola cambió (US4 · T048): llega por el stream (`queue_position`) o por la
   * respuesta de la señal de espera. **No reinicia nada**: a diferencia de `challenge_queued`, no
   * toca la colección de frames, porque la cámara puede haber leído ya los primeros del turno que
   * acaba de empezar.
   */
  | { type: 'queue_position'; position: number; etaMs: number }
  /** El challenge encolado pasó a reproducirse (US4 lo alimenta desde el stream o del latido). */
  | { type: 'challenge_displaying' }
  /** Una lectura ya incorporada a la colección. */
  | { type: 'frames'; frames: FrameCollection }
  /** Una lectura `FM1A` (modo autónomo, US8 · T061) ya incorporada. Entra desde `SEARCHING` (el
   * primer frame autónomo leído) o desde `CHALLENGE` con `autonomous` en `true`. */
  | { type: 'autonomous_frames'; frames: AutonomousCollection }
  | { type: 'verifying' }
  | { type: 'verified'; mark: AttendanceMarkConfirmation }
  | { type: 'failed'; error: MarkingError }
  /** `POST /challenges` respondió `ACTION_CHANGED` (FR-007): la acción vigente cambió antes de
   * crear ningún challenge. `action` es la que el servidor dice que corresponde ahora. */
  | { type: 'action_changed'; action: AttendanceAction }
  /** El trabajador confirmó la acción corregida: vuelve a buscar el marcador con ella (US2, T036). */
  | { type: 'confirm_action_changed'; now: number }
  /** Tick de 1 s mientras se busca: alimenta la ayuda de los 8 s y el reintento de los 20 s. */
  | { type: 'tick'; now: number }
  /** "Reintentar": vuelve a buscar sin cerrar la cámara. */
  | { type: 'retry'; now: number }
  /**
   * T087/FR-045: una verificación autónoma se rechazó por evidencia insuficiente y `use-marking.ts`
   * decidió que corresponde el reintento automático (el primero de este intento). Limpia la
   * evidencia acumulada y programa la reanudación para `now + AUTONOMOUS_RETRY_DELAY_MS`.
   */
  | { type: 'autonomous_retry_scheduled'; now: number }
  /** T087: se cumplió el plazo de espera; se reanuda la captura de frames autónomos nuevos. */
  | { type: 'autonomous_retry_resumed' };

export interface InitialMarkingOptions {
  proposedAction?: AttendanceAction | null;
  now?: number;
}

export function createInitialMarkingState(options: InitialMarkingOptions = {}): MarkingState {
  return {
    phase: 'OPENING',
    proposedAction: options.proposedAction ?? null,
    challenge: null,
    queue: null,
    frames: emptyCollection(),
    autonomous: false,
    autonomousFrames: null,
    autonomousRetryUntil: null,
    idempotencyKey: null,
    mark: null,
    error: null,
    cameraFailure: null,
    searchStartedAt: null,
    nowMs: options.now ?? 0,
    failedAttempts: 0,
    failureAt: [],
    retryDisabledUntil: null,
  };
}

/** Forma del challenge que necesita el acumulador de frames. */
export function challengeShapeOf(challenge: ChallengeRef): ChallengeShape {
  return {
    shortId: challenge.short_id,
    frameCount: challenge.frames_count,
    requiredFrames: challenge.required_frames,
  };
}

/** FR-005: a los 8 s buscando se ofrece la ayuda ("Separa un poco el teléfono"). */
export function shouldShowHelp(state: MarkingState): boolean {
  return (
    state.phase === 'SEARCHING' &&
    state.searchStartedAt !== null &&
    state.nowMs - state.searchStartedAt >= HELP_AFTER_MS
  );
}

/** FR-005: a los 20 s buscando se ofrece reintentar. */
export function shouldOfferRetry(state: MarkingState): boolean {
  return (
    state.phase === 'SEARCHING' &&
    state.searchStartedAt !== null &&
    state.nowMs - state.searchStartedAt >= RETRY_AFTER_MS
  );
}

/**
 * US7 AC4 / FR-035: tras tres challenges fallidos **consecutivos en 2 minutos** se sugiere
 * contactar al administrador. `state.failedAttempts` ya cuenta "consecutivos" (se resetea al
 * verificar con éxito); aquí se exige además que caigan dentro de `SUPPORT_WINDOW_MS`.
 */
export function shouldSuggestSupport(state: MarkingState): boolean {
  if (state.failedAttempts < FAILED_ATTEMPTS_BEFORE_SUPPORT) {
    return false;
  }
  const recent = state.failureAt.filter((at) => state.nowMs - at <= SUPPORT_WINDOW_MS);
  return recent.length >= FAILED_ATTEMPTS_BEFORE_SUPPORT;
}

/**
 * T087/FR-045: `true` mientras Staff espera el reintento automático de una verificación autónoma
 * rechazada por evidencia insuficiente. La cámara sigue abierta y leyendo; los frames que lleguen
 * se descartan hasta que se cumpla `autonomousRetryUntil`.
 */
export function isRetryingAutonomous(state: MarkingState): boolean {
  return state.autonomousRetryUntil !== null;
}

/** T087: pasado el plazo (`nowMs` del reloj, no `state.nowMs`, que puede no haberse refrescado aún). */
export function shouldResumeAutonomousRetry(state: MarkingState, nowMs: number): boolean {
  return state.autonomousRetryUntil !== null && nowMs >= state.autonomousRetryUntil;
}

/**
 * T094: `true` mientras "Intentar nuevamente" debe quedar deshabilitado (`RATE_LIMITED` /
 * `TERMINAL_BUSY` con `retry_after_seconds`). El tick de 500 ms (`MARKING_TICK_MS`) sigue
 * corriendo en cualquier fase, así que `state.nowMs` avanza también durante `ERROR`.
 */
export function isRetryDisabled(state: MarkingState): boolean {
  return state.retryDisabledUntil !== null && state.nowMs < state.retryDisabledUntil;
}

/** T094: segundos restantes (redondeados hacia arriba) de la espera de `isRetryDisabled`; `0` si no hay ninguna. */
export function retryDisabledSeconds(state: MarkingState): number {
  if (state.retryDisabledUntil === null) {
    return 0;
  }
  return Math.max(0, Math.ceil((state.retryDisabledUntil - state.nowMs) / 1_000));
}

/**
 * Avance 0–100 de "Verificando…"; `undefined` fuera de ese estado. Desde QA-4 de la 005 lo pinta
 * la barra fina bajo el marco (`focus-frame.tsx`), y `undefined` significa "sin barra" (ya no hay
 * anillo indeterminado). Se conserva el nombre para no mover a los llamadores.
 */
export function ringProgress(state: MarkingState): number | undefined {
  if (state.autonomous) {
    if (state.phase === 'VERIFYING') {
      return 100;
    }
    // T087: en espera del reintento automático no hay barra (antes, anillo indeterminado) — es, en los
    // hechos, "buscando de nuevo" — en vez de mostrar 0 % de una evidencia que se descartó.
    if (state.autonomousRetryUntil !== null) {
      return undefined;
    }
    if (state.phase !== 'CHALLENGE' || state.autonomousFrames === null) {
      return undefined;
    }
    return autonomousProgress(state.autonomousFrames);
  }
  if (state.challenge === null) {
    return undefined;
  }
  if (state.phase === 'VERIFYING') {
    return 100;
  }
  if (state.phase !== 'CHALLENGE') {
    return undefined;
  }
  return evidenceProgress(state.frames, challengeShapeOf(state.challenge));
}

/** Vuelve a buscar marcador conservando la acción propuesta y el contador de fallos. */
function searching(state: MarkingState, now: number): MarkingState {
  return {
    ...state,
    phase: 'SEARCHING',
    challenge: null,
    queue: null,
    frames: emptyCollection(),
    autonomous: false,
    autonomousFrames: null,
    autonomousRetryUntil: null,
    idempotencyKey: null,
    error: null,
    retryDisabledUntil: null,
    searchStartedAt: now,
    nowMs: now,
  };
}

export function markingReducer(state: MarkingState, event: MarkingEvent): MarkingState {
  switch (event.type) {
    case 'request_camera':
      return { ...state, phase: 'OPENING', error: null, cameraFailure: null };

    case 'camera_opened':
      return searching(state, event.now);

    case 'camera_failed':
      return {
        ...state,
        phase: 'CAMERA_BLOCKED',
        cameraFailure: event.failure,
        error: event.error,
        searchStartedAt: null,
        retryDisabledUntil: null,
      };

    case 'bootstrap_found':
      if (state.phase !== 'SEARCHING') {
        return state;
      }
      return { ...state, phase: 'REQUESTING', searchStartedAt: null };

    case 'challenge_created':
      return {
        ...state,
        phase: 'CHALLENGE',
        challenge: event.challenge,
        queue: null,
        frames: emptyCollection(),
        autonomous: false,
        autonomousFrames: null,
        autonomousRetryUntil: null,
        idempotencyKey: event.idempotencyKey,
        error: null,
        retryDisabledUntil: null,
      };

    case 'challenge_queued':
      return {
        ...state,
        phase: 'QUEUED',
        challenge: event.challenge,
        queue: { position: event.position, etaMs: event.etaMs },
        frames: emptyCollection(),
        autonomous: false,
        autonomousFrames: null,
        autonomousRetryUntil: null,
        error: null,
        retryDisabledUntil: null,
      };

    case 'queue_position':
      if (state.phase !== 'QUEUED') {
        return state;
      }
      if (state.queue?.position === event.position && state.queue.etaMs === event.etaMs) {
        return state;
      }
      return { ...state, queue: { position: event.position, etaMs: event.etaMs } };

    case 'challenge_displaying':
      if (state.challenge === null) {
        return state;
      }
      return { ...state, phase: 'CHALLENGE', queue: null };

    case 'frames':
      if (state.phase !== 'CHALLENGE' && state.phase !== 'QUEUED') {
        return state;
      }
      // Un frame leído mientras seguía la espera significa que el turno ya llegó.
      return { ...state, phase: 'CHALLENGE', queue: null, frames: event.frames };

    case 'autonomous_frames':
      // Entra desde `SEARCHING` (primer frame `FM1A` leído) o sigue mientras `autonomous` ya es
      // `true`; un frame autónomo llegando en medio de un challenge normal se ignora (US8/T061).
      if (state.phase !== 'SEARCHING' && !(state.phase === 'CHALLENGE' && state.autonomous)) {
        return state;
      }
      // T087: en espera del reintento automático los frames se descartan aquí también — defensa en
      // profundidad; `use-marking.ts` ya no debería despachar este evento mientras se espera.
      if (state.autonomousRetryUntil !== null) {
        return state;
      }
      return {
        ...state,
        phase: 'CHALLENGE',
        autonomous: true,
        challenge: null,
        queue: null,
        autonomousFrames: event.frames,
        searchStartedAt: null,
        error: null,
      };

    case 'verifying':
      return { ...state, phase: 'VERIFYING' };

    case 'verified':
      return {
        ...state,
        phase: 'SUCCESS',
        mark: event.mark,
        error: null,
        queue: null,
        searchStartedAt: null,
        autonomousRetryUntil: null,
        failedAttempts: 0,
        failureAt: [],
        retryDisabledUntil: null,
      };

    case 'failed':
      return {
        ...state,
        phase: 'ERROR',
        error: event.error,
        queue: null,
        searchStartedAt: null,
        failedAttempts: state.failedAttempts + 1,
        // Se poda a la ventana vigente antes de añadir el nuevo fallo (US7 AC4: memoria acotada).
        failureAt: [
          ...state.failureAt.filter((at) => state.nowMs - at <= SUPPORT_WINDOW_MS),
          state.nowMs,
        ],
        // T094: `RATE_LIMITED`/`TERMINAL_BUSY` traen cuánto esperar; cualquier otro código deja
        // "Intentar nuevamente" habilitado de inmediato, como siempre.
        retryDisabledUntil:
          event.error.retryAfterSeconds === undefined
            ? null
            : state.nowMs + event.error.retryAfterSeconds * 1_000,
      };

    case 'action_changed':
      return {
        ...state,
        phase: 'ACTION_CHANGED',
        proposedAction: event.action,
        challenge: null,
        queue: null,
        frames: emptyCollection(),
        autonomous: false,
        autonomousFrames: null,
        autonomousRetryUntil: null,
        idempotencyKey: null,
        error: null,
        searchStartedAt: null,
        retryDisabledUntil: null,
      };

    case 'confirm_action_changed':
      if (state.phase !== 'ACTION_CHANGED') {
        return state;
      }
      // `state.proposedAction` ya quedó actualizado al entrar en `ACTION_CHANGED`: `searching`
      // conserva el resto del estado y vuelve a buscar el marcador con la acción corregida.
      return searching(state, event.now);

    case 'tick':
      // QA-7: la espera de un `429` terminó ⇒ fuera el error, listo para marcar (sin leer solo).
      if (
        state.phase === 'ERROR' &&
        state.retryDisabledUntil !== null &&
        event.now >= state.retryDisabledUntil
      ) {
        return {
          ...state,
          phase: 'READY',
          error: null,
          retryDisabledUntil: null,
          nowMs: event.now,
        };
      }
      return state.nowMs === event.now ? state : { ...state, nowMs: event.now };

    case 'retry':
      return searching(state, event.now);

    case 'autonomous_retry_scheduled':
      // Llega desde `VERIFYING` (la evidencia ya había salido cuando el servidor la rechazó): solo
      // tiene sentido en medio de un intento autónomo; fuera de ahí (el trabajador ya salió de la
      // pantalla) no hay nada que reintentar.
      if (!state.autonomous) {
        return state;
      }
      return {
        ...state,
        phase: 'CHALLENGE',
        autonomousFrames: emptyAutonomousCollection(),
        idempotencyKey: null,
        error: null,
        autonomousRetryUntil: event.now + AUTONOMOUS_RETRY_DELAY_MS,
      };

    case 'autonomous_retry_resumed':
      if (state.autonomousRetryUntil === null) {
        return state;
      }
      return { ...state, autonomousRetryUntil: null };
  }
}
